import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, CreditCard } from 'lucide-react';
import { MembershipActions } from '@/components/organisms/memberships/MembershipActions';
import { StatusBadge } from '@/components/molecules/StatusBadge';
import {
  EntityListCardItem,
  EntityListCompactItem,
  EntitySplitListPanel,
} from '@/components/organisms';
import { DataTableFilterSection } from '@/components/organisms/DataTableFilterSection';
import {
  DataTableSearchFilterField,
  DataTableSelectFilterField,
  EntityListAdvancedFiltersSheet,
  EntityListCountFooter,
} from '@/components/molecules';
import { useListPanelSearch } from '@/hooks/lib/useListPanelState';
import { useMemberships } from '@/hooks/memberships/useMemberships';
import {
  formatMembershipBilling,
  formatMembershipDate,
  formatMembershipPlanName,
  formatMembershipShortDate,
  MEMBERSHIP_PAGE_SIZE,
  MEMBERSHIP_QUEUE_FILTERS,
  MEMBERSHIP_SOURCE_FILTERS,
  MEMBERSHIP_STATUS_DOT_COLORS,
  MEMBERSHIP_STATUS_FILTERS,
  type MembershipListFilters,
  type MembershipQueueFilter,
  type MembershipSourceFilter,
} from '@/lib/memberships/membership-list-model';
import type { MembershipStatus } from '@/types/membership';

type SortOption = {
  label: string;
  sortBy: string;
  sortDir: 'asc' | 'desc';
  icon: ReactNode;
};

// eslint-disable-next-line react-refresh/only-export-components
export const MEMBERSHIP_SORT_OPTIONS: SortOption[] = [
  { label: 'Created', sortBy: 'createdAt', sortDir: 'desc', icon: <ArrowDown size={12} /> },
  { label: 'Created', sortBy: 'createdAt', sortDir: 'asc', icon: <ArrowUp size={12} /> },
  { label: 'Next bill', sortBy: 'nextBillingDate', sortDir: 'asc', icon: <ArrowUp size={12} /> },
  { label: 'Billing', sortBy: 'billingAmount', sortDir: 'desc', icon: <ArrowDown size={12} /> },
  { label: 'Member #', sortBy: 'membershipNumber', sortDir: 'desc', icon: <ArrowDown size={12} /> },
  { label: 'Updated', sortBy: 'updatedAt', sortDir: 'desc', icon: <ArrowDown size={12} /> },
];

interface MembershipListPanelProps {
  selectedId?: string;
  onSelect: (id: string) => void;
  autoSelectFirst?: boolean;
  compact?: boolean;
  filters: MembershipListFilters;
  onFiltersChange: (patch: Partial<MembershipListFilters>) => void;
}

export function MembershipListPanel({
  selectedId,
  onSelect,
  autoSelectFirst = false,
  compact = false,
  filters,
  onFiltersChange,
}: MembershipListPanelProps) {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const { searchInput, setSearchInput, debouncedSearch } = useListPanelSearch({
    value: filters.q,
    onChange: (value) => onFiltersChange({ q: value, page: 1 }),
  });

  const activeSort = MEMBERSHIP_SORT_OPTIONS[filters.sort] ?? MEMBERSHIP_SORT_OPTIONS[0];

  const query = useMemberships({
    page: filters.page,
    limit: MEMBERSHIP_PAGE_SIZE,
    ...(filters.status !== 'all' ? { status: filters.status } : {}),
    ...(filters.source !== 'all' ? { source: filters.source } : {}),
    ...(filters.queue !== 'all' ? { queue: filters.queue } : {}),
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
    sort: activeSort.sortDir === 'desc' ? `-${activeSort.sortBy}` : activeSort.sortBy,
  });

  const memberships = useMemo(() => query.data?.data ?? [], [query.data?.data]);
  const total = query.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / MEMBERSHIP_PAGE_SIZE));

  useEffect(() => {
    if (!autoSelectFirst || query.isLoading || query.isError || memberships.length === 0) return;
    if (selectedId && memberships.some((membership) => membership.id === selectedId)) return;
    onSelect(memberships[0].id);
  }, [autoSelectFirst, memberships, onSelect, query.isError, query.isLoading, selectedId]);

  const activeAdvancedCount =
    (filters.q.trim() ? 1 : 0) +
    (filters.status !== 'all' ? 1 : 0) +
    (filters.source !== 'all' ? 1 : 0) +
    (filters.queue !== 'all' ? 1 : 0);

  function resetAdvancedFilters() {
    onFiltersChange({
      q: '',
      status: 'all',
      source: 'all',
      queue: 'all',
      page: 1,
    });
    setSearchInput('');
  }

  return (
    <EntitySplitListPanel
      search={{
        value: searchInput,
        onChange: setSearchInput,
        placeholder: 'Search memberships...',
      }}
      statusFilter={{
        value: filters.status,
        options: MEMBERSHIP_STATUS_FILTERS,
        dotColors: MEMBERSHIP_STATUS_DOT_COLORS,
        onChange: (value) =>
          onFiltersChange({ status: value as MembershipStatus | 'all', page: 1 }),
      }}
      sort={{
        activeIndex: filters.sort,
        activeLabel: activeSort.label,
        options: MEMBERSHIP_SORT_OPTIONS,
        onSelect: (index) => onFiltersChange({ sort: index, page: 1 }),
        onReset: () => onFiltersChange({ sort: 0, page: 1 }),
      }}
      toolbarEnd={
        <EntityListAdvancedFiltersSheet
          open={filtersOpen}
          onOpenChange={setFiltersOpen}
          activeCount={activeAdvancedCount}
          onReset={resetAdvancedFilters}
          resetDisabled={activeAdvancedCount === 0}
        >
          <DataTableFilterSection
            label="Membership Number"
            active={!!filters.q.trim()}
            onClear={() => onFiltersChange({ q: '', page: 1 })}
          >
            <DataTableSearchFilterField
              value={filters.q}
              onValueChange={(value) => onFiltersChange({ q: value, page: 1 })}
              placeholder="Search number..."
            />
          </DataTableFilterSection>

          <DataTableFilterSection
            label="Status"
            active={filters.status !== 'all'}
            onClear={() => onFiltersChange({ status: 'all', page: 1 })}
          >
            <DataTableSelectFilterField
              value={filters.status}
              onValueChange={(value) =>
                onFiltersChange({ status: value as MembershipStatus | 'all', page: 1 })
              }
              placeholder="All statuses"
              options={MEMBERSHIP_STATUS_FILTERS.map((filter) => ({
                value: filter.value,
                label: filter.value === 'all' ? 'All statuses' : filter.label,
              }))}
            />
          </DataTableFilterSection>

          <DataTableFilterSection
            label="Source"
            active={filters.source !== 'all'}
            onClear={() => onFiltersChange({ source: 'all', page: 1 })}
          >
            <DataTableSelectFilterField
              value={filters.source}
              onValueChange={(value) =>
                onFiltersChange({ source: value as MembershipSourceFilter, page: 1 })
              }
              placeholder="All sources"
              options={MEMBERSHIP_SOURCE_FILTERS.map((filter) => ({
                value: filter.value,
                label: filter.value === 'all' ? 'All sources' : filter.label,
              }))}
            />
          </DataTableFilterSection>

          <DataTableFilterSection
            label="Queue"
            active={filters.queue !== 'all'}
            onClear={() => onFiltersChange({ queue: 'all', page: 1 })}
          >
            <DataTableSelectFilterField
              value={filters.queue}
              onValueChange={(value) =>
                onFiltersChange({ queue: value as MembershipQueueFilter, page: 1 })
              }
              placeholder="All queues"
              options={MEMBERSHIP_QUEUE_FILTERS.map((filter) => ({
                value: filter.value,
                label: filter.value === 'all' ? 'All queues' : filter.label,
              }))}
            />
          </DataTableFilterSection>
        </EntityListAdvancedFiltersSheet>
      }
      isLoading={query.isLoading}
      isError={query.isError}
      onRetry={() => void query.refetch()}
      emptyIcon={CreditCard}
      emptyTitle="No memberships found"
      hasItems={memberships.length > 0}
      pagination={
        !query.isLoading && total > MEMBERSHIP_PAGE_SIZE
          ? {
              page: filters.page,
              totalPages,
              total,
              onPrevious: () => onFiltersChange({ page: filters.page - 1 }),
              onNext: () => onFiltersChange({ page: filters.page + 1 }),
            }
          : undefined
      }
      footer={
        !query.isLoading && total <= MEMBERSHIP_PAGE_SIZE ? (
          <EntityListCountFooter count={total} singular="membership" />
        ) : null
      }
    >
      {memberships.map((membership) => {
        if (compact) {
          return (
            <EntityListCompactItem
              key={membership.id}
              selected={selectedId === membership.id}
              onClick={() => onSelect(membership.id)}
              content={
                <div className="min-w-0">
                  <div className="flex items-center justify-between gap-1.5">
                    <p className="truncate text-sm font-medium">
                      {formatMembershipPlanName(membership)}
                    </p>
                    <StatusBadge status={membership.status} />
                  </div>
                  <div className="mt-0.5 flex items-center gap-1.5">
                    <span className="font-mono text-xs text-muted-foreground">
                      #{membership.membershipNumber}
                    </span>
                    {membership.nextBillingDate ? (
                      <>
                        <span className="text-xs text-muted-foreground">·</span>
                        <span className="text-xs text-muted-foreground">
                          {formatMembershipShortDate(membership.nextBillingDate)}
                        </span>
                      </>
                    ) : null}
                    <span className="ml-auto text-xs font-semibold">
                      {formatMembershipBilling(membership)}
                    </span>
                  </div>
                </div>
              }
              actions={<MembershipActions membership={membership} variant="row" />}
            />
          );
        }

        return (
          <EntityListCardItem
            key={membership.id}
            onClick={() => onSelect(membership.id)}
            content={
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="truncate text-sm font-medium">
                    {formatMembershipPlanName(membership)}
                  </p>
                  <StatusBadge status={membership.status} />
                </div>
                <div className="mt-0.5 flex items-center gap-2">
                  <span className="font-mono text-xs text-muted-foreground">
                    #{membership.membershipNumber}
                  </span>
                  {membership.nextBillingDate ? (
                    <>
                      <span className="text-xs text-muted-foreground">·</span>
                      <span className="text-xs text-muted-foreground">
                        Next: {formatMembershipDate(membership.nextBillingDate)}
                      </span>
                    </>
                  ) : null}
                </div>
              </div>
            }
            value={formatMembershipBilling(membership)}
            actions={<MembershipActions membership={membership} variant="row" />}
          />
        );
      })}
    </EntitySplitListPanel>
  );
}
